import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { Clips, type ClipsListResponse } from '@air/api';
import { type AssetFileKind, type Clip, type ClipsListOptions } from '@air/api/types';

export const CLIPS_LIST_KEY = '@plugin-ui/CLIPS_LIST';

export const getClipsListKey = (params?: UseClipsListParams) => {
  return [CLIPS_LIST_KEY, params];
};

export type UseClipsListParams = ClipsListOptions & {
  workspaceId: string | undefined;
  /** Optional list of asset types to filter by (e.g., 'image', 'video', 'pdf') */
  assetTypes?: AssetFileKind[];
  /** Optional list of file extensions to filter by (e.g., 'jpg', 'png', 'pdf') */
  fileExtensions?: string[];
};

export const CLIPS_LIST_DEFAULT_PARAMS: Partial<UseClipsListParams> = {
  limit: 48,
};

export const useClipsList = (params: UseClipsListParams) => {
  const { assetTypes, fileExtensions, ...restParams } = params;

  const assetTypeFilter = assetTypes?.length ? { assetFileKind: { or: assetTypes } } : {};
  const extensionFilter = fileExtensions?.length ? { extension: { or: fileExtensions } } : {};

  const mergedParams = {
    ...CLIPS_LIST_DEFAULT_PARAMS,
    ...restParams,
    filters: {
      ...assetTypeFilter,
      ...extensionFilter,
      ...restParams.filters,
    },
  };
  const { workspaceId } = mergedParams;

  return useInfiniteQuery({
    getNextPageParam: (lastPage: ClipsListResponse<Clip>) => lastPage?.pagination?.cursor,
    initialPageParam: '',
    queryFn: workspaceId
      ? ({ pageParam }) =>
          Clips.search({
            ...mergedParams,
            cursor: pageParam || null,
            workspaceId,
          })
      : skipToken,
    queryKey: getClipsListKey(mergedParams),
  });
};
